import React from "react";
import { playFreq, playTick } from "@/lib/audio";

const rand = (a) => a[Math.floor(Math.random() * a.length)];
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const freq = (midi) => 440 * 2 ** ((midi - 69) / 12);
const Listen = ({ text }) => <p className="text-sm text-muted-foreground">{text}</p>;

// All ear-game sound goes through here so it can be cut off when the question changes.
let timers = [];
let voices = [];
export const stopEar = () => { timers.forEach(clearTimeout); voices.forEach((s) => s && s()); timers = []; voices = []; };
const at = (ms, fn) => timers.push(setTimeout(fn, ms));
const tone = (midi, d, v) => voices.push(playFreq(freq(midi), d, v));

const INTERVALS = [["minor 2nd", 1], ["major 2nd", 2], ["minor 3rd", 3], ["major 3rd", 4], ["perfect 4th", 5], ["tritone", 6],
  ["perfect 5th", 7], ["minor 6th", 8], ["major 6th", 9], ["minor 7th", 10], ["major 7th", 11], ["octave", 12]];
export const intervalGame = {
  id: "ear-intervals", label: "Interval listening", tip: "Listen to two notes, one after the other. Which interval is it?",
  make: (prev) => {
    let it; do { it = rand(INTERVALS); } while (prev && prev.key === it[0]);
    const root = 60 + Math.floor(Math.random() * 8);
    const play = () => { stopEar(); tone(root, 0.8); at(800, () => tone(root + it[1], 0.9)); };
    const choices = shuffle([it[0], ...shuffle(INTERVALS.filter((x) => x[0] !== it[0])).slice(0, 3).map((x) => x[0])]);
    return { key: it[0], prompt: <Listen text="Which interval did you hear?" />, choices, check: (c) => c === it[0], reveal: [it[0]], play, stop: stopEar, auto: true };
  },
};

const CHORDS = [["Major", [0, 4, 7]], ["Minor", [0, 3, 7]], ["Diminished", [0, 3, 6]], ["Augmented", [0, 4, 8]], ["Sus 2", [0, 2, 7]], ["Sus 4", [0, 5, 7]]];
export const chordGame = {
  id: "ear-chords", label: "Chord listening", tip: "Listen to the chord (all together, then one note at a time). Is it major, minor, diminished, augmented, sus 2 or sus 4?",
  make: (prev) => {
    let ch; do { ch = rand(CHORDS); } while (prev && prev.key === ch[0]);
    const root = 55 + Math.floor(Math.random() * 8);
    const play = () => {
      stopEar();
      ch[1].forEach((s) => tone(root + s, 1.2, 0.6));
      ch[1].forEach((s, i) => at(1400 + i * 450, () => tone(root + s, 0.6)));
    };
    return { key: ch[0], prompt: <Listen text="Which type of chord did you hear?" />, choices: CHORDS.map((c) => c[0]), check: (c) => c === ch[0], reveal: [ch[0]], play, stop: stopEar, auto: true };
  },
};

const RHYTHMS = {
  r1: [1, 1, 1, 1], r2: [2, 1, 1], r3: [1, 1, 2], r4: [0.5, 0.5, 1, 1, 1], r5: [1, 0.5, 0.5, 1, 1],
  r6: [1, 1, 0.5, 0.5, 1], r7: [2, 2], r8: [1.5, 0.5, 1, 1], r9: [0.5, 0.5, 0.5, 0.5, 1, 1], r10: [4], r11: [3, 1], r12: [1, 0.5, 0.5, 0.5, 0.5, 1],
};

// One bar drawn on a single line: notes placed by beat, eighth pairs beamed, stems up.
const RhythmView = ({ id }) => {
  const X0 = 22, BW = 44, Y = 34, TOP = 8;
  let beat = 0;
  const notes = RHYTHMS[id].map((d) => { const n = { d, x: X0 + beat * BW }; beat += d; return n; });
  const beams = [];
  notes.forEach((n, i) => { if (n.d === 0.5 && notes[i + 1]?.d === 0.5 && Number.isInteger((n.x - X0) / BW)) beams.push([n.x, notes[i + 1].x]); });
  const beamed = new Set(beams.flat());
  return (
    <svg viewBox="0 0 200 50" className="h-12 w-48" data-testid={`rhythm-${id}`} role="img" aria-label={`Rhythm: ${RHYTHMS[id].join(", ")} beats`}>
      <line x1="6" y1={Y} x2="194" y2={Y} stroke="currentColor" strokeOpacity="0.4" />
      <line x1="194" y1={Y - 12} x2="194" y2={Y + 12} stroke="currentColor" strokeOpacity="0.6" />
      {notes.map((n, i) => (
        <g key={i} fill="currentColor" stroke="currentColor">
          <ellipse cx={n.x} cy={Y} rx="6" ry="4.4" transform={`rotate(-20 ${n.x} ${Y})`} fill={n.d >= 2 ? "none" : "currentColor"} strokeWidth="1.5" />
          {n.d < 4 && <line x1={n.x + 5.5} y1={Y - 1} x2={n.x + 5.5} y2={TOP} strokeWidth="1.5" />}
          {(n.d === 1.5 || n.d === 3) && <circle cx={n.x + 11} cy={Y - 1} r="1.8" stroke="none" />}
          {n.d === 0.5 && !beamed.has(n.x) && <path d={`M ${n.x + 5.5} ${TOP} q 4 8 9 12`} fill="none" strokeWidth="1.6" />}
        </g>
      ))}
      {beams.map(([a, b]) => <rect key={a} x={a + 5.5} y={TOP} width={b - a} height="4" fill="currentColor" />)}
    </svg>
  );
};

const BEAT = 750;
const playRhythm = (id) => {
  stopEar();
  [0, 1, 2, 3].forEach((k) => at(100 + k * BEAT, () => playTick(700, 0.04, 0.5)));
  let t = 100 + 4 * BEAT;
  RHYTHMS[id].forEach((b) => { at(t, () => playTick(1500, 0.06, 1)); t += b * BEAT; });
};
export const rhythmGame = {
  id: "ear-rhythm", label: "Rhythm listening", tip: "You'll hear 4 count-in clicks, then a one-bar rhythm. Which one did you hear?",
  make: (prev) => {
    const ids = Object.keys(RHYTHMS);
    let id; do { id = rand(ids); } while (prev && prev.key === id);
    const choices = shuffle([id, ...shuffle(ids.filter((x) => x !== id)).slice(0, 2)]);
    return { key: id, prompt: <Listen text="Which rhythm did you hear?" />, choices, render: (c) => <RhythmView id={c} />, check: (c) => c === id, reveal: [id], play: () => playRhythm(id), stop: stopEar, auto: true };
  },
};

export const EAR_GAMES = [intervalGame, chordGame, rhythmGame];
